import { ValuationDossier as ValuationDossierType } from '../../types/dossier';
import { RestorationPhase } from '../../types/teardown';
import {
  formatCurrency,
  formatShortDate,
  getProvenanceBandInfo,
  triggerPrint,
  exportDossierJson,
} from '../../utils/printFormatter';
import { DossierSummaryTable } from './DossierSummaryTable';
import { DossierPhotoGrid } from './DossierPhotoGrid';
import { ConditionBadge } from '../common/ConditionBadge';
import {
  ShieldCheck,
  Printer,
  Download,
  FileCheck,
  Wrench,
  CheckCircle,
  FileText,
  Car,
  Layers,
  Award,
  Camera,
} from 'lucide-react';

export interface ValuationDossierProps {
  dossier: ValuationDossierType;
  onPrint?: () => void;
  onExportJson?: () => void;
  className?: string;
  isPrintMode?: boolean;
}

export const ValuationDossier: React.FC<ValuationDossierProps> = ({
  dossier,
  onPrint,
  onExportJson,
  className = '',
  isPrintMode = false,
}) => {
  const { vehicle, valuation, phases, bomHighlights, subletExpenses, curatedPhotos, underwritingDeclarations } = dossier;
  const scoreInfo = getProvenanceBandInfo(valuation.provenanceScore.totalScore);

  const handlePrint = () => {
    if (onPrint) onPrint();
    else triggerPrint();
  };

  const handleExportJson = () => {
    if (onExportJson) onExportJson();
    else exportDossierJson(dossier);
  };

  const phaseEntries = Object.entries(phases) as [
    RestorationPhase,
    ValuationDossierType['phases'][RestorationPhase]
  ][];

  return (
    <div
      className={`dossier-print-container max-w-5xl mx-auto space-y-8 bg-shop-dark text-shop-light p-4 sm:p-8 rounded border-2 border-shop-border shadow-tactile ${className}`}
    >
      {/* =====================================================================
          ACTION TOOLBAR (Hidden in Print)
          ===================================================================== */}
      <div className="no-print bg-shop-darkest border border-shop-border p-4 rounded flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-shop-panel border border-shop-border flex items-center justify-center text-safety-amber">
            <Award className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold font-mono uppercase text-shop-highlight">
              Agreed-Value Insurance Valuation Dossier
            </h3>
            <p className="text-xs text-shop-muted">
              Standard Collector Underwriting Document Formatted for{' '}
              <strong className="text-safety-amber">
                {underwritingDeclarations.intendedUnderwriter}
              </strong>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExportJson}
            className="min-h-[44px] min-w-[44px] px-4 py-2 bg-shop-panel text-shop-light hover:text-shop-highlight border border-shop-border hover:bg-shop-well rounded font-mono text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4 text-blueprint-cyan" />
            <span>Export JSON</span>
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="min-h-[44px] min-w-[44px] px-5 py-2 bg-safety-amber hover:bg-[#fbbf24] text-shop-darkest border border-safety-amberDark rounded font-mono text-xs font-bold uppercase tracking-wider transition-transform active:translate-y-0.5 shadow-[0_2px_0_0_#92400e] flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            <span>Print Official Dossier</span>
          </button>
        </div>
      </div>

      {/* =====================================================================
          1. FORMAL APPRAISAL HEADER & OFFICIAL UNDERWRITING CERTIFICATE
          ===================================================================== */}
      <header className="border-b-2 border-shop-border pb-6 space-y-4 avoid-break">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold uppercase tracking-widest text-safety-amber bg-safety-amber/10 px-2 py-0.5 rounded border border-safety-amber/40">
                OFFICIAL UNDERWRITING DOSSIER
              </span>
              <span className="text-xs font-mono text-shop-muted">
                DOC ID: RMS-{vehicle.id.toUpperCase()}-{new Date().getFullYear()}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold font-mono text-shop-highlight uppercase tracking-tight">
              {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim || ''}
            </h1>
            <p className="text-xs text-shop-muted font-mono">
              Prepared by: <strong className="text-shop-light">{underwritingDeclarations.preparedBy}</strong> |{' '}
              Date: <strong className="text-shop-light">{formatShortDate(dossier.generatedAt)}</strong>
            </p>
          </div>

          <div className="sm:text-right space-y-1 shrink-0">
            <div className="text-[11px] font-mono text-shop-muted uppercase">
              SUBMITTED UNDERWRITER
            </div>
            <div className="text-lg font-bold font-mono text-blueprint-cyan uppercase">
              {underwritingDeclarations.intendedUnderwriter}
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-shop-darkest border border-safety-green/50 text-safety-green font-mono text-xs font-bold">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>CERTIFIED APPRAISAL BASIS</span>
            </div>
          </div>
        </div>

        {/* Valuation Banner Highlight */}
        <div className="bg-shop-darkest border-2 border-safety-amber p-4 rounded grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          <div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-shop-muted block">
              TOTAL INVESTED CAPITAL BASIS
            </span>
            <span className="text-xl sm:text-2xl font-bold font-mono text-shop-highlight">
              {formatCurrency(valuation.totalInvestedCapital)}
            </span>
          </div>

          <div className="sm:border-x border-shop-border">
            <span className="text-[10px] font-mono uppercase tracking-wider text-safety-amber block">
              RECOMMENDED AGREED VALUE (+{valuation.appreciationBufferPct}% Buffer)
            </span>
            <span className="text-2xl sm:text-3xl font-bold font-mono text-safety-amber">
              {formatCurrency(valuation.suggestedAgreedValue)}
            </span>
          </div>

          <div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-blueprint-cyan block">
              PROVENANCE COMPLETENESS
            </span>
            <span className="text-xl sm:text-2xl font-bold font-mono text-blueprint-cyan">
              {valuation.provenanceScore.totalScore} / 100
            </span>
            <span className="text-[10px] font-bold block" style={{ color: scoreInfo.badgeColor }}>
              {valuation.provenanceScore.ratingBand}
            </span>
          </div>
        </div>
      </header>

      {/* =====================================================================
          2. VEHICLE IDENTIFICATION & TECHNICAL SPECIFICATIONS PLATE
          ===================================================================== */}
      <section className="space-y-4 avoid-break">
        <div className="flex items-center gap-2 border-b border-shop-border pb-2">
          <Car className="w-4 h-4 text-safety-blue" />
          <h2 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
            Section 1: Vehicle Identification &amp; Technical Specifications
          </h2>
        </div>

        {/* VIN & Cowl Tag Decode Card */}
        <div className="bg-shop-darkest border border-shop-border rounded p-4 space-y-3 font-mono">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-shop-border pb-2.5">
            <div>
              <span className="text-[10px] text-shop-muted block">VEHICLE IDENTIFICATION NUMBER (VIN)</span>
              <span className="text-base sm:text-lg font-bold text-shop-highlight tracking-widest">
                {vehicle.vin || 'VIN NOT SPECIFIED'}
              </span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-safety-green/10 text-safety-green border border-safety-green/40 rounded text-xs font-bold">
              <CheckCircle className="w-3.5 h-3.5" />
              <span>STAMPED CHASSIS VERIFIED</span>
            </div>
          </div>

          {vehicle.cowlTag && (
            <div>
              <span className="text-[10px] text-shop-muted block">
                COWL / FENDER DATA TAG DECODE:
              </span>
              <p className="text-xs text-shop-light bg-shop-panel/70 p-2.5 rounded border border-shop-border mt-1 whitespace-pre-wrap leading-relaxed">
                {vehicle.cowlTag}
              </p>
            </div>
          )}
        </div>

        {/* Specifications Matrix Table */}
        <div className="bg-shop-dark border border-shop-border rounded overflow-hidden">
          <table className="w-full text-left border-collapse text-xs">
            <tbody className="divide-y divide-shop-border font-mono">
              <tr className="hover:bg-shop-well/30">
                <th className="py-2.5 px-4 bg-shop-darkest text-shop-muted w-1/3 uppercase font-normal">
                  Engine &amp; Induction
                </th>
                <td className="py-2.5 px-4 text-shop-highlight font-sans font-medium">
                  {vehicle.engineSpecs || 'Factory Specifications'}
                </td>
              </tr>
              <tr className="hover:bg-shop-well/30">
                <th className="py-2.5 px-4 bg-shop-darkest text-shop-muted w-1/3 uppercase font-normal">
                  Transmission &amp; Drivetrain
                </th>
                <td className="py-2.5 px-4 text-shop-highlight font-sans font-medium">
                  {vehicle.transmissionSpecs || 'Factory Specifications'}
                </td>
              </tr>
              <tr className="hover:bg-shop-well/30">
                <th className="py-2.5 px-4 bg-shop-darkest text-shop-muted w-1/3 uppercase font-normal">
                  Chassis &amp; Suspension
                </th>
                <td className="py-2.5 px-4 text-shop-highlight font-sans font-medium">
                  {vehicle.chassisSuspensionSpecs || 'Factory Specifications'}
                </td>
              </tr>
              <tr className="hover:bg-shop-well/30">
                <th className="py-2.5 px-4 bg-shop-darkest text-shop-muted w-1/3 uppercase font-normal">
                  Braking Systems
                </th>
                <td className="py-2.5 px-4 text-shop-highlight font-sans font-medium">
                  {vehicle.brakeSpecs || 'Factory Specifications'}
                </td>
              </tr>
              <tr className="hover:bg-shop-well/30">
                <th className="py-2.5 px-4 bg-shop-darkest text-shop-muted w-1/3 uppercase font-normal">
                  Paint Code &amp; Finish
                </th>
                <td className="py-2.5 px-4 text-shop-highlight font-sans font-medium">
                  {vehicle.paintCodeColor || 'Standard Factory Finish'}
                </td>
              </tr>
              <tr className="hover:bg-shop-well/30">
                <th className="py-2.5 px-4 bg-shop-darkest text-shop-muted w-1/3 uppercase font-normal">
                  Interior &amp; Upholstery
                </th>
                <td className="py-2.5 px-4 text-shop-highlight font-sans font-medium">
                  {vehicle.interiorColorTrim || 'Standard Factory Trim'}
                </td>
              </tr>
              <tr className="hover:bg-shop-well/30">
                <th className="py-2.5 px-4 bg-shop-darkest text-shop-muted w-1/3 uppercase font-normal">
                  Odometer / Acquisition
                </th>
                <td className="py-2.5 px-4 text-shop-highlight font-mono">
                  {vehicle.odometerReading ? `${vehicle.odometerReading.toLocaleString()} Miles` : 'N/A'}{' '}
                  | Acquired: {formatShortDate(vehicle.acquisitionDate)} ({formatCurrency(vehicle.acquisitionCost)})
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* =====================================================================
          3. UNDERWRITING PROVENANCE SCORECARD
          ===================================================================== */}
      <section className="space-y-4 avoid-break">
        <div className="flex items-center gap-2 border-b border-shop-border pb-2">
          <ShieldCheck className="w-4 h-4 text-safety-green" />
          <h2 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
            Section 2: Underwriting Provenance Completeness Scorecard ({valuation.provenanceScore.totalScore}/100)
          </h2>
        </div>

        <div className="bg-shop-dark border border-shop-border p-4 rounded space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-center text-xs font-mono">
            <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
              <span className="text-[10px] text-shop-muted block truncate">VIN &amp; Cowl Tag</span>
              <span className="font-bold text-shop-highlight text-sm">
                {valuation.provenanceScore.vinVerified.earned} / {valuation.provenanceScore.vinVerified.max}
              </span>
            </div>

            <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
              <span className="text-[10px] text-shop-muted block truncate">7-Phase Coverage</span>
              <span className="font-bold text-shop-highlight text-sm">
                {valuation.provenanceScore.phaseCoverage.earned} / {valuation.provenanceScore.phaseCoverage.max}
              </span>
            </div>

            <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
              <span className="text-[10px] text-shop-muted block truncate">Photo Plates</span>
              <span className="font-bold text-shop-highlight text-sm">
                {valuation.provenanceScore.photoEvidence.earned} / {valuation.provenanceScore.photoEvidence.max}
              </span>
            </div>

            <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
              <span className="text-[10px] text-shop-muted block truncate">Receipt Trail</span>
              <span className="font-bold text-shop-highlight text-sm">
                {valuation.provenanceScore.receiptAuditTrail.earned} / {valuation.provenanceScore.receiptAuditTrail.max}
              </span>
            </div>

            <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
              <span className="text-[10px] text-shop-muted block truncate">Hardware Catalog</span>
              <span className="font-bold text-shop-highlight text-sm">
                {valuation.provenanceScore.hardwareCataloging.earned} / {valuation.provenanceScore.hardwareCataloging.max}
              </span>
            </div>

            <div className="bg-shop-darkest p-2.5 rounded border border-shop-border">
              <span className="text-[10px] text-shop-muted block truncate">Sublet Machining</span>
              <span className="font-bold text-shop-highlight text-sm">
                {valuation.provenanceScore.subletDocumentation.earned} / {valuation.provenanceScore.subletDocumentation.max}
              </span>
            </div>
          </div>

          <p className="text-xs text-shop-light font-sans leading-relaxed border-t border-shop-border pt-3">
            <strong className="font-mono text-safety-amber uppercase">{scoreInfo.band}:</strong>{' '}
            {scoreInfo.description}
          </p>
        </div>
      </section>

      {/* =====================================================================
          4. 7-PHASE RESTORATION LIFECYCLE REBUILD LEDGER
          ===================================================================== */}
      <section className="space-y-4 avoid-break">
        <div className="flex items-center gap-2 border-b border-shop-border pb-2">
          <Layers className="w-4 h-4 text-safety-amber" />
          <h2 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
            Section 3: 7-Phase Restoration Lifecycle Progress Ledger
          </h2>
        </div>

        <div className="bg-shop-dark border border-shop-border rounded overflow-hidden">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-shop-panel text-[11px] font-mono text-shop-muted uppercase border-b border-shop-border">
                <th className="py-2.5 px-3">Phase</th>
                <th className="py-2.5 px-3">Scope Description</th>
                <th className="py-2.5 px-2 text-center">Tasks</th>
                <th className="py-2.5 px-2 text-center">Bins</th>
                <th className="py-2.5 px-2 text-center">Baggies</th>
                <th className="py-2.5 px-2 text-center">BOM Parts</th>
                <th className="py-2.5 px-2 text-center">Photos</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-shop-border font-mono">
              {phaseEntries.map(([pKey, p]) => {
                return (
                  <tr key={pKey} className="hover:bg-shop-well/30 transition-colors">
                    <td className="py-2.5 px-3 font-bold text-shop-highlight">
                      <span className="text-[10px] text-safety-amber block">{p.metadata.shortCode}</span>
                      {p.metadata.title}
                    </td>
                    <td className="py-2.5 px-3 text-shop-muted font-sans text-[11px] max-w-xs">
                      {p.metadata.description}
                    </td>
                    <td className="py-2.5 px-2 text-center text-shop-light font-bold">
                      {p.completedTaskCount}/{p.taskCount}
                    </td>
                    <td className="py-2.5 px-2 text-center text-shop-muted">
                      {p.subAssemblyCount}
                    </td>
                    <td className="py-2.5 px-2 text-center text-shop-muted">
                      {p.hardwareBagCount}
                    </td>
                    <td className="py-2.5 px-2 text-center text-blueprint-cyan font-bold">
                      {p.bomItemCount}
                    </td>
                    <td className="py-2.5 px-2 text-center text-safety-green font-bold">
                      {p.photoCount}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* =====================================================================
          5. FINANCIAL CAPITAL INVESTMENT LEDGER
          ===================================================================== */}
      <section className="space-y-4 avoid-break">
        <div className="flex items-center gap-2 border-b border-shop-border pb-2">
          <FileText className="w-4 h-4 text-safety-green" />
          <h2 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
            Section 4: Itemized Capital Investment Ledger &amp; Valuation Basis
          </h2>
        </div>

        <DossierSummaryTable
          valuation={valuation}
          bomItems={dossier.bomHighlights}
          subletExpenses={subletExpenses}
          laborRecords={[]}
          isPrintMode={isPrintMode}
        />
      </section>

      {/* =====================================================================
          6. SPECIALIST SUBLET SERVICES & MACHINING REGISTER
          ===================================================================== */}
      {subletExpenses.length > 0 && (
        <section className="space-y-4 avoid-break">
          <div className="flex items-center gap-2 border-b border-shop-border pb-2">
            <Wrench className="w-4 h-4 text-safety-amber" />
            <h2 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
              Section 5: Specialist Sublet Machining &amp; Finishing Invoices ({subletExpenses.length} Invoices)
            </h2>
          </div>

          <div className="bg-shop-dark border border-shop-border rounded overflow-hidden">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-shop-panel text-[11px] font-mono text-shop-muted uppercase border-b border-shop-border">
                  <th className="py-2.5 px-3">Vendor / Service Provider</th>
                  <th className="py-2.5 px-3">Service Category</th>
                  <th className="py-2.5 px-3">Invoice # / Date</th>
                  <th className="py-2.5 px-3">Scope of Work</th>
                  <th className="py-2.5 px-3 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-shop-border font-mono">
                {subletExpenses.map((sublet) => (
                  <tr key={sublet.id} className="hover:bg-shop-well/30 transition-colors">
                    <td className="py-2.5 px-3 font-bold text-shop-highlight font-sans">
                      {sublet.vendorName}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="bg-shop-darkest px-2 py-0.5 rounded border border-shop-border text-[10px] text-blueprint-cyan">
                        {sublet.serviceCategory}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-shop-muted">
                      <div className="font-bold text-shop-light">{sublet.invoiceNumber}</div>
                      <div className="text-[10px]">{formatShortDate(sublet.invoiceDate)}</div>
                    </td>
                    <td className="py-2.5 px-3 text-shop-light font-sans text-[11px] max-w-sm">
                      {sublet.description}
                    </td>
                    <td className="py-2.5 px-3 text-right font-bold text-shop-highlight">
                      {formatCurrency(sublet.cost, { showCents: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-shop-darkest text-xs font-mono font-bold border-t border-shop-border">
                  <td colSpan={4} className="py-2.5 px-3 text-shop-highlight uppercase">
                    Total Sublet Invoices
                  </td>
                  <td className="py-2.5 px-3 text-right text-safety-amber">
                    {formatCurrency(valuation.totalSubletLaborCost, { showCents: true })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}

      {/* =====================================================================
          7. MAJOR BOM PROCUREMENT HIGHLIGHTS
          ===================================================================== */}
      {bomHighlights.length > 0 && (
        <section className="space-y-4 avoid-break">
          <div className="flex items-center gap-2 border-b border-shop-border pb-2">
            <FileCheck className="w-4 h-4 text-safety-blue" />
            <h2 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
              Section 6: Major Capital Component Highlights (Top Documented Line Items)
            </h2>
          </div>

          <div className="bg-shop-dark border border-shop-border rounded overflow-hidden">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-shop-panel text-[11px] font-mono text-shop-muted uppercase border-b border-shop-border">
                  <th className="py-2.5 px-3">Part Name</th>
                  <th className="py-2.5 px-2 text-center">Condition</th>
                  <th className="py-2.5 px-3">Supplier / Invoice</th>
                  <th className="py-2.5 px-2 text-center">Qty</th>
                  <th className="py-2.5 px-3 text-right">Net Actual Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-shop-border font-mono">
                {bomHighlights.map((item) => {
                  const netCost =
                    (item.actualCost || item.estimatedCost || 0) * (item.quantity || 1) +
                    (item.shippingCost || 0) +
                    (item.taxCost || 0) +
                    (item.coreCharge || 0) -
                    (item.coreRefunded || 0);

                  return (
                    <tr key={item.id} className="hover:bg-shop-well/30 transition-colors">
                      <td className="py-2.5 px-3 font-bold text-shop-highlight font-sans">
                        {item.partName}
                        {item.partNumber && (
                          <span className="text-[10px] font-mono text-shop-muted block">
                            P/N: {item.partNumber}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <ConditionBadge condition={item.condition} size="sm" />
                      </td>
                      <td className="py-2.5 px-3 text-shop-light font-sans">
                        <div>{item.vendor}</div>
                        {item.receiptNumber && (
                          <span className="text-[10px] font-mono text-shop-muted">
                            Rec: {item.receiptNumber}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-center text-shop-muted">
                        {item.quantity || 1}
                      </td>
                      <td className="py-2.5 px-3 text-right font-bold text-shop-highlight">
                        {formatCurrency(netCost, { showCents: true })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* =====================================================================
          8. PHOTOGRAPHIC RESTORATION EVIDENCE PLATES
          ===================================================================== */}
      {curatedPhotos.length > 0 && (
        <section className="space-y-4 avoid-break">
          <div className="flex items-center gap-2 border-b border-shop-border pb-2">
            <Camera className="w-4 h-4 text-safety-green" />
            <h2 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
              Section 7: Photographic Restoration Evidence Plates ({curatedPhotos.length} Documented Plates)
            </h2>
          </div>

          <DossierPhotoGrid
            photos={curatedPhotos}
            filterCuratedOnly={false}
            isPrintMode={isPrintMode}
          />
        </section>
      )}

      {/* =====================================================================
          9. UNDERWRITING DECLARATIONS & LEGAL SIGN-OFF BLOCK
          ===================================================================== */}
      <section className="space-y-6 pt-4 border-t-2 border-shop-border avoid-break">
        <div className="flex items-center gap-2 border-b border-shop-border pb-2">
          <ShieldCheck className="w-4 h-4 text-safety-amber" />
          <h2 className="text-xs font-bold font-mono uppercase tracking-wider text-shop-highlight">
            Section 8: Underwriting Declarations &amp; Policyholder Certification
          </h2>
        </div>

        <div className="bg-shop-darkest border border-shop-border p-4 rounded space-y-4 font-mono text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-b border-shop-border pb-4">
            <div>
              <span className="text-[10px] text-shop-muted uppercase block">
                STORAGE FACILITY TYPE:
              </span>
              <span className="font-bold text-shop-highlight">
                {underwritingDeclarations.storageFacilityType}
              </span>
            </div>
            <div>
              <span className="text-[10px] text-shop-muted uppercase block">
                ANNUAL ESTIMATED MILEAGE:
              </span>
              <span className="font-bold text-shop-highlight">
                {underwritingDeclarations.annualEstimatedMileage.toLocaleString()} Miles / Year
              </span>
            </div>
            <div>
              <span className="text-[10px] text-shop-muted uppercase block">
                INTENDED UNDERWRITER:
              </span>
              <span className="font-bold text-safety-amber">
                {underwritingDeclarations.intendedUnderwriter} Classic Program
              </span>
            </div>
          </div>

          <div className="space-y-2 text-[11px] text-shop-muted font-sans leading-relaxed">
            <p>
              <strong>AFFIDAVIT OF PROVENANCE:</strong> I hereby certify that the specifications,
              VIN stamping records, parts invoices, and photographic milestones compiled in this
              dossier represent an authentic, actual-cost accounting of the vehicle restoration.
              All parts and professional sublet receipts are kept on file and available for
              physical underwriting inspection.
            </p>
          </div>

          {/* Signature Lines */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-8 font-mono text-xs">
            <div className="space-y-2">
              <div className="border-b border-shop-border h-12 flex items-end pb-1 text-shop-muted italic">
                {underwritingDeclarations.preparedBy}
              </div>
              <div className="flex justify-between text-[10px] text-shop-muted">
                <span>CERTIFIED APPRAISER / RESTORER SIGNATURE</span>
                <span>DATE: {formatShortDate(dossier.generatedAt)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="border-b border-shop-border h-12 flex items-end pb-1 text-shop-muted italic">
                Registered Vehicle Owner / Policyholder
              </div>
              <div className="flex justify-between text-[10px] text-shop-muted">
                <span>REGISTERED OWNER SIGNATURE</span>
                <span>DATE: {formatShortDate(underwritingDeclarations.ownerSignatureDate)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer Footer */}
        <p className="text-[10px] text-shop-muted font-mono leading-relaxed text-center">
          RestoMod Studio Valuation Engine | Documented Provenance Standard compliant with
          Hagerty, Grundy Worldwide, American Collectors Insurance, and auction authenticity requirements.
        </p>
      </section>
    </div>
  );
};
